// Supplier documents (invoices, receipts, quotes): which files can be read, getting one ready for Claude, and the small
// text helpers the Costs and Quote to budget pages share.
import { fmt } from './theme.js';

export const INV_MEDIA = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const INV_MAX_MB = 4;
export function readFileB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });
}
export function normTxt(s) {
  return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}
export function suggestLine(sections, supplier) {
  const toks = normTxt(supplier).split(' ').filter(t => t.length >= 3);
  if (!toks.length) return null;
  let best = null,
    bestScore = 0;
  sections.forEach(sec => sec.items.forEach(it => {
    const sup = ' ' + normTxt(it.sup) + ' ',
      desc = ' ' + normTxt(it.desc) + ' ';
    const score = toks.reduce((s, t) => s + (sup.includes(' ' + t + ' ') ? 2 : 0) + (desc.includes(' ' + t + ' ') ? 1 : 0), 0);
    if (score > bestScore) {
      best = it;
      bestScore = score;
    }
  }));
  return bestScore >= 2 ? best : null;
}
export function imageForReading(file, maxEdge = 2400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file),
      img = new Image();
    img.onload = () => {
      const k = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.round(img.naturalWidth * k);
      c.height = Math.round(img.naturalHeight * k);
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', quality).split(',')[1]);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not open the image'));
    };
    img.src = url;
  });
}
export const numStr = v => v == null || v === '' ? '' : String(v);
export const money = (n, c) => !isFinite(n) ? '—' : (c ? c + ' ' : '') + fmt.num(n, 2);
// Checks a chosen or dropped file and gets it ready: onStart(file) as soon as it can be shown, then a photo is shrunk
// to the copy Claude reads, which is also the copy that's stored (onShrunk). Returns { b64, type } to send for
// reading, or null when the file can't be used (the reason has been shown with flash).
export async function takeDocument(f, { flash, onStart, onShrunk }) {
  if (!f) return null;
  if (!INV_MEDIA.includes(f.type)) {
    flash(`${f.name}: use a PDF, JPG, PNG or WebP. Export iPhone HEIC photos as JPG first.`, 'err');
    return null;
  }
  if (f.size > (f.type === 'application/pdf' ? INV_MAX_MB : 25) * 1024 * 1024) {
    flash(f.type === 'application/pdf' ? `${f.name} is over ${INV_MAX_MB} MB. Compress the PDF first.` : `${f.name} is over 25 MB`, 'err');
    return null;
  }
  let b64;
  try { b64 = await readFileB64(f); } catch (e) { flash(e.message, 'err'); return null; }
  onStart({ name: f.name, type: f.type, b64, url: URL.createObjectURL(f) });
  if (f.type === 'application/pdf') return { b64, type: f.type };
  try {
    const small = await imageForReading(f);
    onShrunk({ b64: small, type: 'image/jpeg' });
    return { b64: small, type: 'image/jpeg' };
  } catch (_) { return { b64, type: f.type }; }
}
